import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Animated keyword component for staggered color reveal
// Mobile-optimized hero header
const AnimatedKeyword = ({
  children,
  colorClass,
  hoverClass,
  delay,
  onClick
}: {
  children: React.ReactNode;
  colorClass: string;
  hoverClass: string;
  delay: number;
  onClick: () => void;
}) => {
  return (
    <motion.span
      className={`${colorClass} cursor-pointer ${hoverClass} transition-colors inline-block`}
      initial={{ filter: "grayscale(100%)", opacity: 0.5 }}
      animate={{ filter: "grayscale(0%)", opacity: 1 }}
      transition={{ duration: 0.35, delay, ease: "easeOut" }}
      onClick={onClick}
    >
      {children}
    </motion.span>
  );
};

interface HeroHeaderProps {
  scrollToSection: (sectionId: string) => void;
}

export const HeroHeader = ({ scrollToSection }: HeroHeaderProps) => {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-bold text-3xl sm:text-6xl lg:text-8xl tracking-tight mb-4 sm:mb-8">
          We help{" "}
          <AnimatedKeyword
            colorClass="text-primary"
            hoverClass="hover:text-primary/80"
            delay={0.2}
            onClick={() => scrollToSection('manage-section')}
          >
            manage
          </AnimatedKeyword>
          {" "}and{" "}
          <AnimatedKeyword
            colorClass="text-grow"
            hoverClass="hover:text-grow/80"
            delay={0.55}
            onClick={() => scrollToSection('grow-section')}
          >
            grow
          </AnimatedKeyword>
          {" "}your{" "}
          <AnimatedKeyword
            colorClass="text-secondary"
            hoverClass="hover:text-secondary/80"
            delay={0.9}
            onClick={() => scrollToSection('business-section')}
          >
            business
          </AnimatedKeyword>
        </h1>
        <motion.p
          className="mt-4 sm:mt-6 text-base sm:text-xl lg:text-2xl max-w-3xl mx-auto px-4 sm:px-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1.3, ease: "easeOut" }}
        >
          Tag-Inn is platform for business owners who look to
        </motion.p>
        <motion.p
          className="text-base sm:text-xl lg:text-2xl max-w-3xl mx-auto px-4 sm:px-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1.45, ease: "easeOut" }}
        >
          manage employees, grow your visibility, gain loyal customers from one Inn.
        </motion.p>
        <motion.div
          className="mt-8 sm:mt-12 flex items-center justify-center gap-x-6 flex-wrap gap-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35, delay: 1.6, ease: "easeOut" }}
        >
          <Button size="lg" className="px-8 bg-primary hover:bg-primary/90 text-lg" asChild>
            <Link to="/sign-up">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </div>
  );
};
